import { Fragment, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MultiTenant from "../MultiTenant";
import {
	Box,
	Typography,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	Paper,
	Avatar,
	Menu,
	MenuItem,
	Divider,
	AppBar,
	Toolbar,
    useTheme
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { UserContext } from "../lib";
import useApi from "../hooks/useApi";

/**
 * NavbarLeft
 * ----------
 * Left navigation sidebar with a modern "carded" visual style and consistent alignment.
 *
 * Updates:
 * - Mobile/Topbar Coordination: Designed to work with the simplified NavbarTop. On small screens,
 *   a compact header bar is rendered at the top that includes a hamburger menu on the left along
 *   with the brand logo and site name. This keeps branding visible and provides an obvious affordance
 *   to open/close the sidebar.
 *
 * - Slide-over (No Backdrop): Removed the semi-transparent backdrop overlay. The sidebar now slides
 *   over the content without dimming the background. The mobile top bar remains clickable so users
 *   can toggle the menu at any time.
 *
 * - Sticky Account Footer: The user profile/avatar and actions (Profile, Logout) are available in
 *   a sticky footer at the bottom of the sidebar.
 *
 * - Visual Consistency: Each actionable row (parent triggers, leaves, children) is placed
 *   in a Paper "card" for consistent elevation, hover, and active styling.
 *
 * - Divider Support: Menu options can declare `dividerBefore` or `dividerAfter` flags to render
 *   full-width horizontal dividers that separate logical sections (e.g., admin vs. case tools).
 *
 * Structural ordering:
 * - Mobile Header: Compact top bar with hamburger + brand (mobile only)
 * - Header: MultiTenant selector (inside the sidebar)
 * - Menu: Primary navigation items (optional groups with children)
 * - Footer: Sticky account section with avatar and actions
 *
 * Props:
 * - menu: Menu configuration
 * - logo: Brand logo source (URL or data URI) for the mobile header
 * - siteName: Application/site name for the mobile header
 *
 * Layout details:
 * - The mobile header is position: fixed; the main application content area should include a top
 *   padding equal to the header height on small screens to avoid overlap. The AdminLayout applies
 *   this responsive padding.
 * - The sidebar is offset below the mobile header on small screens so they do not overlap.
 */
export const NavbarLeft = ({ menu, logo, siteName }) => {
	const [expandedItems, setExpandedItems] = useState({});
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [anchorElAccount, setAnchorElAccount] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const { user } = useContext(UserContext);
    const theme = useTheme();
	const api = useApi();

	// Constants for consistent icon spacing across all menu items
	const ICON_MIN_WIDTH = 40;
	const ICON_PLACEHOLDER_SIZE = 24;

	// Compact mobile header height
	const MOBILE_HEADER_HEIGHT = 56;

	/**
	 * Returns an icon container for list items.
	 * - Horizontal orientation (default): Uses MUI's ListItemIcon to place the icon at the left,
	 *   ensuring consistent min-width alignment.
	 * - Vertical orientation (not used in this layout but retained for potential reuse): Provides
	 *   a fixed-size box, typically for stacking icons above labels.
	 *
	 * @param {React.ReactNode} icon - Optional icon to render.
	 * @param {boolean} [vertical=false] - Whether to render the icon for a vertical stack.
	 * @returns {React.ReactElement} The icon element or placeholder.
	 */
	const renderListItemIcon = (icon, vertical = false) => {
		if (vertical) {
			return (
				<Box
					sx={{
						width: ICON_PLACEHOLDER_SIZE,
						height: ICON_PLACEHOLDER_SIZE,
						color: "inherit",
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					{icon ?? null}
				</Box>
			);
		}
		return (
			<ListItemIcon sx={{ color: "inherit", minWidth: ICON_MIN_WIDTH }}>
				{icon ?? <Box sx={{ width: ICON_PLACEHOLDER_SIZE, height: ICON_PLACEHOLDER_SIZE }} />}
			</ListItemIcon>
		);
	};

	// Auto-expand parent menus if the current path matches a submenu
	useEffect(() => {
		if (menu?.options) {
			const updatedExpandedItems = {};
			menu.options.forEach((option, index) => {
				if (
					option.children?.some((child) =>
						location.pathname.includes(child.url)
					)
				) {
					updatedExpandedItems[index] = true;
				}
			});
			setExpandedItems(updatedExpandedItems);
		}
	}, [location.pathname, menu]);

	/**
	 * Toggles the expansion state of a menu item.
	 *
	 * @param {number} index - The index of the menu item to toggle.
	 */
	const handleToggle = (index) => {
		setExpandedItems((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	/**
	 * Toggles the mobile menu open or close.
	 */
	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	/**
	 * Helper to compute if a URL should be considered active for styling.
	 * Uses "includes" to allow parent paths to match sub-routes.
	 *
	 * @param {string} url - The URL to compare against current location.
	 * @returns {boolean} Active state.
	 */
	const isPathActive = (url) => {
		if (!url) return false;
		return location.pathname.includes(url);
	};

	/**
	 * Styles for Paper "card" containers to create lift (elevation), rounded corners,
	 * hover feedback, and an optional active accent. Nested cards (children) get slightly
	 * lighter visuals and additional left margin for hierarchy.
	 *
	 * @param {boolean} active - Whether the item is active.
	 * @param {boolean} nested - Whether the item is a nested (child) item.
	 * @returns {Object} sx styles for the Paper component.
	 */
	const getCardSx = (active, nested = false) => ({
		position: "relative",
		width: "100%",
		borderRadius: 2,
		boxShadow: (theme) => theme.shadows[active ? 6 : 1],
		bgcolor: active ? "grey.100" : "background.paper",
		transition: "box-shadow 200ms ease, transform 100ms ease, background-color 200ms ease",
		"&:hover": {
			boxShadow: (theme) => theme.shadows[8],
			transform: "translateY(-1px)"
		},
		// Active left accent bar
		"&::before": {
			content: '""',
			position: "absolute",
			left: 0,
			top: 0,
			bottom: 0,
			width: 3,
			bgcolor: "primary.main",
			opacity: active ? 1 : 0,
			transition: "opacity 200ms ease"
		},
		// Hierarchy spacing/indent
		...(nested
			? { ml: 2 }
			: {})
	});

	/**
	 * Account footer menu handlers
	 */
	const openAccountMenu = (event) => setAnchorElAccount(event.currentTarget);
	const closeAccountMenu = () => setAnchorElAccount(null);

	const handleProfile = () => {
		closeAccountMenu();
		setIsMobileMenuOpen(false);
		navigate('/profile');
	};

	const handleLogout = async () => {
		closeAccountMenu();
		try {
			await api.user.logout();
		} catch (e) {
			// Non-blocking: log and continue redirect
			console.error('Logout encountered an error:', e);
		}
		// Hard redirect to ensure full state reset
		window.location.href = '/';
	};

	return (
		<>
			{/* Mobile Header - Compact bar with hamburger + brand (visible on small screens) */}
			<AppBar
				elevation={3}
				color="default"
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					height: `${MOBILE_HEADER_HEIGHT}px`,
					display: { xs: "flex", lg: "none" },
					justifyContent: "center",
					bgcolor: "background.paper",
					borderBottom: "1px solid",
					borderColor: "grey.300",
					zIndex: (theme) => theme.zIndex.appBar
				}}
			>
				<Toolbar
					variant="dense"
					sx={{
						height: `${MOBILE_HEADER_HEIGHT}px`,
						minHeight: `${MOBILE_HEADER_HEIGHT}px !important`,
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
						px: 1.5,
						gap: 1.25
					}}
				>
					<IconButton
						onClick={toggleMobileMenu}
						aria-label="Toggle Menu"
						size="small"
						sx={{
							color: "text.primary"
						}}
					>
						<Box
							component="svg"
							sx={{ width: 24, height: 24, color: "inherit" }}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
						</Box>
					</IconButton>

					{/* Brand: Logo + Site Name */}
					{logo && (
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<img
								src={logo}
								alt={siteName ? `${siteName} logo` : "Site logo"}
								style={{ height: 28, width: "auto", marginRight: 8 }}
							/>
						</Box>
					)}
					{siteName && (
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{siteName}
						</Typography>
					)}
				</Toolbar>
			</AppBar>

			{/* Sidebar - Slide-over on Mobile (no backdrop) and visible on Large Screens */}
			<Box
				component="header"
				sx={{
					position: { xs: "fixed", lg: "static" },
					top: { xs: `${MOBILE_HEADER_HEIGHT}px`, lg: 0 },
					left: 0,
					height: { xs: `calc(100% - ${MOBILE_HEADER_HEIGHT}px)`, lg: "100%" },
					width: { xs: 256, lg: 300 },
					bgcolor: theme.palette.background.default,
					borderRight: "1px solid",
					borderColor: "grey.400",
					boxShadow: 3,
					overflowY: "auto",
					display: "flex",
					flexDirection: "column",
					transition: "transform 300ms ease-in-out",
					transform: {
						xs: isMobileMenuOpen ? "translateX(0)" : "translateX(-100%)",
						lg: "none"
					},
					// Keep the top bar above the sidebar so the hamburger remains clickable
					zIndex: (theme) => theme.zIndex.appBar - 1
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
					{/* Tenant selector / header */}
					<MultiTenant />

					{/* Navigation List: uses flex to grow and push the account footer down */}
					<List
						sx={{
							color: "black",
							flex: 1,
							px: 2,
							py: 1.5,
							display: "flex",
							flexDirection: "column",
							gap: 1.25
						}}
					>
						{menu &&
							menu.options.map((option, index) => {
								const hasChildren = !!option.children;
								const hasActiveChild = hasChildren
									? option.children.some((child) => isPathActive(child.url))
									: false;
								const isGroupExpanded = !!expandedItems[index];
								const isLeafActive = !hasChildren && isPathActive(option.url);
								const optionKey = option.url || option.name || index;

								const listItemNode = (
									<ListItem
										disablePadding
										sx={{
											// Carded layout: separation handled by Paper cards
											borderBottom: "none",
											px: 0
										}}
									>
										{hasChildren ? (
											/* Group container retains column layout; the parent trigger and each child are separate Paper cards */
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													width: "100%",
													gap: 1 // spacing between parent card and children cards
												}}
											>
												{/* Parent item trigger as a Paper card */}
												<Paper
													elevation={hasActiveChild || isGroupExpanded ? 3 : 1}
													sx={getCardSx(hasActiveChild || isGroupExpanded, false)}
												>
													<ListItemButton
														onClick={() => handleToggle(index)}
														sx={{
															// Horizontal row: Icon -> Text -> Expand/Collapse
															width: "100%",
															alignItems: "center",
															textAlign: "left",
															flexDirection: "row",
															py: 1.25,
															px: 2
														}}
													>
														{renderListItemIcon(option.icon)}
														<ListItemText
															primary={option.name}
															sx={{ mr: 1 }}
															primaryTypographyProps={{
																sx: {
																	ml: 0.5,
																	fontWeight: hasActiveChild ? "bold" : undefined
																}
															}}
														/>
														{/* Indicator: positioned at far right of the parent trigger */}
														<Box
															sx={{
																display: "flex",
																flexDirection: "column",
																alignItems: "center",
																justifyContent: "center",
																ml: 2,
																color: "text.secondary",
																flexShrink: 0
															}}
														>
															{isGroupExpanded ? (
																<>
																	<ExpandLess fontSize="small" />
                                                                </>
															) : (
																<>
																	<ExpandMore fontSize="small" />
                                                                </>
															)}
														</Box>
													</ListItemButton>
												</Paper>

												{/* Children rendered as individual Paper cards beneath the parent */}
												<Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
													<List
														component="div"
														disablePadding
														sx={{
															display: "flex",
															flexDirection: "column",
															gap: 1,
															px: 0
														}}
													>
														{option.children.map((child, childIndex) => {
															const isChildActive = isPathActive(child.url);
															return (
																<ListItem
																	key={childIndex}
																	disablePadding
																	sx={{
																		borderBottom: "none",
																		px: 0
																	}}
																>
																	<Paper
																		elevation={isChildActive ? 3 : 1}
																		sx={getCardSx(isChildActive, true)}
																	>
																		<ListItemButton
																			component={Link}
																			to={child.url}
																			onClick={() => setIsMobileMenuOpen(false)}
																			sx={{
																				// Horizontal row: Icon -> Text
																				width: "100%",
																				justifyContent: "flex-start",
																				textAlign: "left",
																				alignItems: "center",
																				flexDirection: "row",
																				py: 1,
																				px: 2
																			}}
																		>
																			{renderListItemIcon(child.icon)}
																			<ListItemText
																				primary={child.name}
																				primaryTypographyProps={{
																					sx: {
																						ml: 0.5,
																						fontWeight: isChildActive ? "bold" : undefined
																					}
																				}}
																			/>
																		</ListItemButton>
																	</Paper>
																</ListItem>
															);
														})}
													</List>
												</Collapse>
											</Box>
										) : (
											/* Leaf item (no children): Paper "card" wrapping the button */
											<Paper
												elevation={isLeafActive ? 3 : 1}
												sx={getCardSx(isLeafActive, false)}
											>
												<ListItemButton
													component={Link}
													to={option.url}
													onClick={() => setIsMobileMenuOpen(false)}
													sx={{
														// Horizontal row: Icon -> Text
														width: "100%",
														justifyContent: "flex-start",
														textAlign: "left",
														alignItems: "center",
														flexDirection: "row",
														py: 1.25,
														px: 2
													}}
												>
													{renderListItemIcon(option.icon)}
													<ListItemText
														primary={option.name}
														primaryTypographyProps={{
															sx: {
																ml: 0.5,
																fontWeight: isLeafActive ? "bold" : undefined
															}
														}}
													/>
												</ListItemButton>
											</Paper>
										)}
									</ListItem>
								);

								return (
									<Fragment key={optionKey}>
										{option.dividerBefore ? (
											<Divider
												variant="fullWidth"
												sx={{ borderColor: "grey.300" }}
											/>
										) : null}
										{listItemNode}
										{option.dividerAfter ? (
											<Divider
												variant="fullWidth"
												sx={{ borderColor: "grey.300" }}
											/>
										) : null}
									</Fragment>
								);
							})}
					</List>

					{/* Sticky Account Footer */}
					<Box
						sx={{
							pt: 0,
							position: "sticky",
							bottom: 0,
							bgcolor: "grey.300",
							borderTop: "1px solid",
							borderColor: "grey.400"
						}}
					>
						<Paper elevation={2} sx={{ borderRadius: 0 }}>
							<ListItemButton
								onClick={openAccountMenu}
								aria-controls="account-menu"
								aria-haspopup="true"
								sx={{
									width: "100%",
									alignItems: "center",
									textAlign: "left",
									flexDirection: "row",
									py: 1.25,
									px: 2
								}}
							>
								<ListItemIcon sx={{ minWidth: 48 }}>
									<Avatar
										src={user?.profilePicture}
										alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
										sx={{ width: 32, height: 32 }}
									/>
								</ListItemIcon>
								<ListItemText
									primary={
										<Typography variant="body1" sx={{ fontWeight: 600 }}>
											{user?.firstName} {user?.lastName}
										</Typography>
									}
									secondary={
										<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
											{user?.email}
										</Typography>
									}
								/>
								<Box sx={{ ml: 1, color: "text.secondary" }}>
									<ExpandMore />
								</Box>
							</ListItemButton>
						</Paper>

						<Menu
							id="account-menu"
							anchorEl={anchorElAccount}
							open={Boolean(anchorElAccount)}
							onClose={closeAccountMenu}
							anchorOrigin={{ vertical: "top", horizontal: "center" }}
							transformOrigin={{ vertical: "bottom", horizontal: "center" }}
						>
							<MenuItem onClick={handleProfile}>Profile</MenuItem>
							<Divider />
							<MenuItem onClick={handleLogout}>Logout</MenuItem>
						</Menu>
					</Box>
				</Box>
			</Box>
		</>
	);
};

NavbarLeft.propTypes = {
	menu: PropTypes.shape({
		options: PropTypes.arrayOf(
			PropTypes.shape({
				name: PropTypes.string.isRequired,
				url: PropTypes.string,
				icon: PropTypes.node,
				dividerBefore: PropTypes.bool,
				dividerAfter: PropTypes.bool,
				children: PropTypes.arrayOf(
					PropTypes.shape({
						name: PropTypes.string.isRequired,
						url: PropTypes.string.isRequired,
						icon: PropTypes.node,
					})
				)
			})
		)
	}).isRequired,
	logo: PropTypes.string,
	siteName: PropTypes.string
};